import { useQuery } from "@tanstack/react-query";
import type React from "react";

import type { TicketsResolutionTimeProjectDto } from "@/api/types";
import { Skeleton } from "@/design-system/Skeleton";
import { Stack } from "@/design-system/Stack";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { usePortfolioAnalyticsQueries } from "@/queries/portfolio-analytics/queryOptions";
import { useTranslation } from "@/translations";

import { TrendBadge } from "../Badge/TrendBadge";
import { Ticket01Icon } from "../Icons/Ticket01Icon";
import { AnalyticsWidgetCard } from "./AnalyticsWidgetCard";
import {
  ceilingToOneDecimal,
  derivePrimaryDateRangeFromPeriod,
  deriveSecondaryDateRangeFromPeriod,
  formatAnalyticsDatePayload,
} from "./helpers";
import type { AnalyticsWidgetBaseProps } from "./types";
import { useMetricHelp } from "./useMetricHelp";

type TicketResolutionTimeWidgetProps = AnalyticsWidgetBaseProps;

export function TicketResolutionTimeWidget({
  projectIds = [],
  period,
}: TicketResolutionTimeWidgetProps): React.ReactNode {
  const { t } = useTranslation();
  const help = useMetricHelp();
  const portfolioAnalyticsQueries = usePortfolioAnalyticsQueries();

  const secondaryDates = deriveSecondaryDateRangeFromPeriod(period);
  const { data: secondaryData } = useQuery({
    ...portfolioAnalyticsQueries.platformTicketsResolutionTime({
      projectId: projectIds.length === 1 ? projectIds[0] : undefined,
      startDate: formatAnalyticsDatePayload(secondaryDates.startDate),
      endDate: formatAnalyticsDatePayload(secondaryDates.endDate),
    }),
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        ...data,
        projects: data.projects.filter((project) => projectIds.includes(project.projectId)),
      };
    },
  });

  const primaryDates = derivePrimaryDateRangeFromPeriod(period);
  const { data: primaryData, isPending } = useQuery({
    ...portfolioAnalyticsQueries.platformTicketsResolutionTime({
      projectId: projectIds.length === 1 ? projectIds[0] : undefined,
      startDate: primaryDates.startDate ? formatAnalyticsDatePayload(primaryDates.startDate) : undefined,
      endDate: primaryDates.endDate ? formatAnalyticsDatePayload(primaryDates.endDate) : undefined,
    }),
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        ...data,
        projects: data.projects.filter((project) => projectIds.includes(project.projectId)),
      };
    },
  });

  const aggregateData = (projects: TicketsResolutionTimeProjectDto[]) => {
    return projects.reduce(
      (acc, project) => {
        acc.totalResolutionTimeInDays += project.avgResolutionTimeInDays * project.closedTicketCount;
        acc.totalClosedTickets += project.closedTicketCount;

        return acc;
      },
      { totalResolutionTimeInDays: 0, totalClosedTickets: 0 },
    );
  };

  const computeScore = (totalResolutionTimeInDays: number, amountEligibleProjects: number) => {
    if (amountEligibleProjects === 0) return 0;

    return ceilingToOneDecimal(totalResolutionTimeInDays / amountEligibleProjects);
  };

  const primaryAggregatedData = aggregateData(primaryData?.projects ?? []);
  const primaryScore = computeScore(
    primaryAggregatedData.totalResolutionTimeInDays,
    primaryAggregatedData.totalClosedTickets,
  );
  const secondaryAggregatedData = aggregateData(secondaryData?.projects ?? []);
  const secondaryScore = computeScore(
    secondaryAggregatedData.totalResolutionTimeInDays,
    secondaryAggregatedData.totalClosedTickets,
  );
  const hasData = primaryAggregatedData.totalClosedTickets > 0;

  return (
    <AnalyticsWidgetCard
      title={t("component.analytics-widget.ticket-resolution-time.title")}
      titleTooltip={help("component.analytics-widget.ticket-resolution-time.tooltip")}
      icon={<Ticket01Icon />}
      renderBadge={
        hasData
          ? () => (
              <TrendBadge
                referenceValue={secondaryScore}
                value={primaryScore}
                method="percentage"
                shouldRoundUp
                isZeroOmitted
                isTrendInversed
              />
            )
          : undefined
      }
      helperText={t("component.analytics-widget.ticket-resolution-time.helper-text", {
        closedTicketCount: primaryAggregatedData.totalClosedTickets,
      })}
      isLoading={isPending}
      renderSkeleton={() => <WidgetSkeleton />}
    >
      <span className="text-figure-lg text-text-brand-secondary">
        {hasData
          ? t("component.analytics-widget.ticket-resolution-time.value", {
              dayCount: Math.floor(primaryScore),
              hourCount: Math.round((primaryScore % 1) * 24),
            })
          : t("common.label.not-applicable")}
      </span>
    </AnalyticsWidgetCard>
  );
}

function WidgetSkeleton(): React.ReactNode {
  return (
    <Stack gap="md">
      <Stack gap="xs">
        <Skeleton height="10" width="1/4" />
        <Skeleton height="2" />
      </Stack>
      <Skeleton />
    </Stack>
  );
}
